"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import { MatchupHeader } from "fansunited-sports-ui";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useLiveMatchData } from "@/features/live-data/useLiveMatchData";
import { HeaderMarketOdds } from "@/features/odds/components/HeaderMarketOdds";
import type { MarketType } from "@/features/odds/types";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { computeScoreDisplay } from "@/utilities/formatting/scoreDisplay";
import { formatTimeHHMM, formatDateDDMM } from "@/utilities/formatting/date";
import { MatchCompetitionInfo } from "./MatchCompetitionInfo";
import { useMatchPageDataOptional } from "@/views/match/context/MatchPageDataContext";
export type LiveMatchCardVariant = "highlight" | "embedded" | "compact";

/**
 * TeamLogo's pixel `size` scale is a step larger than MatchupHeader's `imageSize`
 * class scale (e.g. TeamLogo "lg" = 48px, EntityDisplay "lg" = w-10 h-10 = 40px).
 * We pick `matchupImageSize` so the library wrapper is ≥ the TeamLogo pixel size,
 * otherwise EntityDisplay's built-in `overflow-hidden` clips the crest.
 * Stacked variant uses a separate size map with plenty of headroom, so any match works.
 */
const VARIANT_CONFIG = {
  highlight: {
    matchupVariant: "stacked" as const,
    teamLogoSize: "lg" as const,
    matchupImageSize: "lg" as const,
    scoreSize: "text-3xl",
    padding: "p-0"
  },
  embedded: {
    matchupVariant: "inline" as const,
    teamLogoSize: "lg" as const,
    matchupImageSize: "xl" as const,
    scoreSize: "text-lg",
    padding: "p-6"
  },
  compact: {
    matchupVariant: "inline" as const,
    teamLogoSize: "md" as const,
    matchupImageSize: "md" as const,
    scoreSize: "text-md",
    padding: "p-4"
  }
};

/**
 * Renders kickoff time/date for upcoming matches. Defers to mount because the
 * formatting is locale-dependent: server renders UTC, client renders local TZ —
 * mismatched values would break hydration.
 */
function UpcomingKickoff(t0) {
  const $ = _c(20);
  const {
    kickoffTime,
    scoreSize
  } = t0;
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setMounted(true);
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== kickoffTime) {
    t3 = kickoffTime instanceof Date ? kickoffTime : new Date(kickoffTime);
    $[2] = kickoffTime;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const kickoffDate = t3;
  let t4;
  if ($[4] !== kickoffDate || $[5] !== mounted) {
    t4 = mounted ? formatTimeHHMM(kickoffDate) : "";
    $[4] = kickoffDate;
    $[5] = mounted;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const time = t4;
  let t5;
  if ($[7] !== kickoffDate || $[8] !== mounted) {
    t5 = mounted && new Date().toDateString() === kickoffDate.toDateString();
    $[7] = kickoffDate;
    $[8] = mounted;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isToday = t5;
  const t6 = `${scoreSize} font-bold min-h-[1.5em]`;
  const t7 = time || "\xA0";
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isToday || $[14] !== kickoffDate || $[15] !== mounted) {
    t9 = mounted && !isToday && <div className="text-sm opacity-70">{formatDateDDMM(kickoffDate)}</div>;
    $[13] = isToday;
    $[14] = kickoffDate;
    $[15] = mounted;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className="text-center">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
interface LiveMatchCardProps {
  /** The match to display */
  match: FUSportsMatchWithNativeId;
  /** Visual variant of the card */
  variant?: LiveMatchCardVariant;
  /** Whether to show betting odds */
  showOdds?: boolean;
  /** Editor-chosen inline odds market. "none" hides odds. Falls back to FT_1X2 if the chosen market has no odds. */
  market?: MarketType | "none";
  /** Whether to show team positions (requires competition context) */
  showPositions?: boolean;
  /** Optional function to get team position from standings */
  getTeamPosition?: (teamId: string) => number | undefined;
  /** Additional class name for the container */
  className?: string;
}

/**
 * Format position as ordinal (e.g., "1st", "2nd", "3rd", "4th")
 */
function formatPosition(position: number | undefined): string {
  if (!position) return "";
  const suffix = ["th", "st", "nd", "rd"];
  const v = position % 100;
  return position + (suffix[(v - 20) % 10] || suffix[v] || suffix[0]);
}

/**
 * LiveMatchCard - Reusable component for displaying live match data with different visual variants.
 * Supports: highlight (full featured), embedded (for articles), compact (minimal)
 */
export const LiveMatchCard: React.FC<LiveMatchCardProps> = t0 => {
  const $ = _c(131);
  const {
    match,
    variant: t1,
    showOdds: t2,
    market: t3,
    showPositions: t4,
    getTeamPosition,
    className: t5
  } = t0;
  const variant = t1 === undefined ? "highlight" : t1;
  const showOdds = t2 === undefined ? true : t2;
  const market = t3 === undefined ? "FT_1X2" : t3;
  const showPositions = t4 === undefined ? false : t4;
  const className = t5 === undefined ? "" : t5;
  const labels = useLabels();
  const pageData = useMatchPageDataOptional();
  const winner = pageData?.winner;
  const liveClock = pageData?.liveClock;
  const statusType = match.status?.type || "";
  const statusCode = (match.status?.code || "") as string;
  const isLive = statusType === "LIVE" || statusCode === "live" || statusCode === "in_progress";
  const isFinished = statusType === "FINISHED";
  const liveDotClass = !liveClock || liveClock.clockStatus === "running" ? "animate-pulse-subtle" : "";
  let t6;
  if ($[0] !== isLive || $[1] !== match) {
    t6 = {
      match,
      enablePolling: isLive
    };
    $[0] = isLive;
    $[1] = match;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const {
    score,
    matchMinute
  } = useLiveMatchData(t6);
  const competitorOne = match.competitorOne;
  const competitorTwo = match.competitorTwo;
  let T0;
  let centerContent;
  let config;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t7;
  let t8;
  let t9;
  if ($[3] !== className || $[4] !== competitorOne.assets?.logo || $[5] !== competitorOne.country?.code || $[6] !== competitorOne.country?.flag || $[7] !== competitorOne.id || $[8] !== competitorOne.metadata?.branding?.primaryColor || $[9] !== competitorOne.metadata?.branding?.secondaryColor || $[10] !== competitorOne.name || $[11] !== competitorTwo.assets?.logo || $[12] !== competitorTwo.country?.code || $[13] !== competitorTwo.country?.flag || $[14] !== competitorTwo.id || $[15] !== competitorTwo.metadata?.branding?.primaryColor || $[16] !== competitorTwo.metadata?.branding?.secondaryColor || $[17] !== competitorTwo.name || $[18] !== getTeamPosition || $[19] !== isFinished || $[20] !== isLive || $[21] !== labels || $[22] !== liveDotClass || $[23] !== match || $[24] !== matchMinute || $[25] !== score?.competitorOne || $[26] !== score?.competitorTwo || $[27] !== showPositions || $[28] !== statusCode || $[29] !== variant || $[30] !== winner) {
    const competitorOnePosition = showPositions && getTeamPosition ? getTeamPosition(competitorOne.id) : undefined;
    const competitorTwoPosition = showPositions && getTeamPosition ? getTeamPosition(competitorTwo.id) : undefined;
    config = VARIANT_CONFIG[variant];
    if (isLive) {
      const t16 = `${config.scoreSize} font-bold`;
      const t17 = score?.competitorOne || "0";
      const t18 = score?.competitorTwo || "0";
      let t19;
      if ($[43] !== t16 || $[44] !== t17 || $[45] !== t18) {
        t19 = <div className={t16}>{t17} - {t18}</div>;
        $[43] = t16;
        $[44] = t17;
        $[45] = t18;
        $[46] = t19;
      } else {
        t19 = $[46];
      }
      const t20 = `text-sm ${liveDotClass} uppercase opacity-70`;
      const t21 = matchMinute ? ` • ${matchMinute}'` : "";
      let t22;
      if ($[47] !== labels.matches.live || $[48] !== t20 || $[49] !== t21) {
        t22 = <div className={t20}>{labels.matches.live}{t21}</div>;
        $[47] = labels.matches.live;
        $[48] = t20;
        $[49] = t21;
        $[50] = t22;
      } else {
        t22 = $[50];
      }
      let t23;
      if ($[51] !== t19 || $[52] !== t22) {
        t23 = <div className="text-center">{t19}{t22}</div>;
        $[51] = t19;
        $[52] = t22;
        $[53] = t23;
      } else {
        t23 = $[53];
      }
      centerContent = t23;
    } else {
      if (isFinished) {
        let t16;
        if ($[54] !== labels.matchPage.status || $[55] !== match.score || $[56] !== statusCode || $[57] !== winner) {
          t16 = computeScoreDisplay(match.score, statusCode, labels.matchPage.status, winner);
          $[54] = labels.matchPage.status;
          $[55] = match.score;
          $[56] = statusCode;
          $[57] = winner;
          $[58] = t16;
        } else {
          t16 = $[58];
        }
        const scoreDisplay = t16;
        const t17 = `${config.scoreSize} font-bold`;
        let t18;
        if ($[59] !== scoreDisplay.scoreOne || $[60] !== scoreDisplay.scoreTwo || $[61] !== t17) {
          t18 = <div className={t17}>{scoreDisplay.scoreOne} - {scoreDisplay.scoreTwo}</div>;
          $[59] = scoreDisplay.scoreOne;
          $[60] = scoreDisplay.scoreTwo;
          $[61] = t17;
          $[62] = t18;
        } else {
          t18 = $[62];
        }
        let t19;
        if ($[63] !== scoreDisplay.statusLabel || $[64] !== scoreDisplay.statusTooltip) {
          t19 = <div className="text-sm uppercase opacity-70" title={scoreDisplay.statusTooltip}>{scoreDisplay.statusLabel}</div>;
          $[63] = scoreDisplay.statusLabel;
          $[64] = scoreDisplay.statusTooltip;
          $[65] = t19;
        } else {
          t19 = $[65];
        }
        let t20;
        if ($[66] !== t18 || $[67] !== t19) {
          t20 = <div className="text-center">{t18}{t19}</div>;
          $[66] = t18;
          $[67] = t19;
          $[68] = t20;
        } else {
          t20 = $[68];
        }
        centerContent = t20;
      } else {
        let t16;
        if ($[69] !== config.scoreSize || $[70] !== match.timing.kickoffTime) {
          t16 = <UpcomingKickoff kickoffTime={match.timing.kickoffTime} scoreSize={config.scoreSize} />;
          $[69] = config.scoreSize;
          $[70] = match.timing.kickoffTime;
          $[71] = t16;
        } else {
          t16 = $[71];
        }
        centerContent = t16;
      }
    }
    t15 = `match-card-surface overflow-hidden ${className}`;
    t14 = "relative overflow-hidden flex flex-col gap-4";
    t12 = `relative ${config.padding}`;
    if ($[72] !== match) {
      t13 = <MatchCompetitionInfo match={match} className="text-muted-foreground mb-3" />;
      $[72] = match;
      $[73] = t13;
    } else {
      t13 = $[73];
    }
    T0 = MatchupHeader;
    t10 = config.matchupVariant;
    const t16 = competitorOne.assets?.logo;
    const t17 = competitorOne.country?.code;
    const t18 = competitorOne.country?.flag;
    const t19 = competitorOne.metadata?.branding?.primaryColor;
    const t20 = competitorOne.metadata?.branding?.secondaryColor;
    let t21;
    if ($[74] !== competitorOne.name || $[75] !== config.teamLogoSize || $[76] !== t16 || $[77] !== t17 || $[78] !== t18 || $[79] !== t19 || $[80] !== t20) {
      t21 = <TeamLogo src={t16} alt={competitorOne.name} size={config.teamLogoSize} countryCode={t17} countryFlag={t18} primaryColor={t19} secondaryColor={t20} />;
      $[74] = competitorOne.name;
      $[75] = config.teamLogoSize;
      $[76] = t16;
      $[77] = t17;
      $[78] = t18;
      $[79] = t19;
      $[80] = t20;
      $[81] = t21;
    } else {
      t21 = $[81];
    }
    const t22 = showPositions ? formatPosition(competitorOnePosition) : undefined;
    let t23;
    if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = {
        name: "font-semibold"
      };
      $[82] = t23;
    } else {
      t23 = $[82];
    }
    let t24;
    if ($[83] !== config.matchupImageSize) {
      t24 = {
        imageBorderRadius: "none",
        imageSize: config.matchupImageSize,
        typography: t23
      };
      $[83] = config.matchupImageSize;
      $[84] = t24;
    } else {
      t24 = $[84];
    }
    if ($[85] !== competitorOne.name || $[86] !== t21 || $[87] !== t22 || $[88] !== t24) {
      t11 = {
        image: t21,
        name: competitorOne.name,
        subtitle: t22,
        styles: t24
      };
      $[85] = competitorOne.name;
      $[86] = t21;
      $[87] = t22;
      $[88] = t24;
      $[89] = t11;
    } else {
      t11 = $[89];
    }
    const t25 = competitorTwo.assets?.logo;
    const t26 = competitorTwo.country?.code;
    const t27 = competitorTwo.country?.flag;
    const t28 = competitorTwo.metadata?.branding?.primaryColor;
    const t29 = competitorTwo.metadata?.branding?.secondaryColor;
    if ($[90] !== competitorTwo.name || $[91] !== config.teamLogoSize || $[92] !== t25 || $[93] !== t26 || $[94] !== t27 || $[95] !== t28 || $[96] !== t29) {
      t7 = <TeamLogo src={t25} alt={competitorTwo.name} size={config.teamLogoSize} countryCode={t26} countryFlag={t27} primaryColor={t28} secondaryColor={t29} />;
      $[90] = competitorTwo.name;
      $[91] = config.teamLogoSize;
      $[92] = t25;
      $[93] = t26;
      $[94] = t27;
      $[95] = t28;
      $[96] = t29;
      $[97] = t7;
    } else {
      t7 = $[97];
    }
    t8 = competitorTwo.name;
    t9 = showPositions ? formatPosition(competitorTwoPosition) : undefined;
    $[3] = className;
    $[4] = competitorOne.assets?.logo;
    $[5] = competitorOne.country?.code;
    $[6] = competitorOne.country?.flag;
    $[7] = competitorOne.id;
    $[8] = competitorOne.metadata?.branding?.primaryColor;
    $[9] = competitorOne.metadata?.branding?.secondaryColor;
    $[10] = competitorOne.name;
    $[11] = competitorTwo.assets?.logo;
    $[12] = competitorTwo.country?.code;
    $[13] = competitorTwo.country?.flag;
    $[14] = competitorTwo.id;
    $[15] = competitorTwo.metadata?.branding?.primaryColor;
    $[16] = competitorTwo.metadata?.branding?.secondaryColor;
    $[17] = competitorTwo.name;
    $[18] = getTeamPosition;
    $[19] = isFinished;
    $[20] = isLive;
    $[21] = labels;
    $[22] = liveDotClass;
    $[23] = match;
    $[24] = matchMinute;
    $[25] = score?.competitorOne;
    $[26] = score?.competitorTwo;
    $[27] = showPositions;
    $[28] = statusCode;
    $[29] = variant;
    $[30] = winner;
    $[31] = T0;
    $[32] = centerContent;
    $[33] = config;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
  } else {
    T0 = $[31];
    centerContent = $[32];
    config = $[33];
    t10 = $[34];
    t11 = $[35];
    t12 = $[36];
    t13 = $[37];
    t14 = $[38];
    t15 = $[39];
    t7 = $[40];
    t8 = $[41];
    t9 = $[42];
  }
  let t16;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      name: "font-semibold"
    };
    $[98] = t16;
  } else {
    t16 = $[98];
  }
  let t17;
  if ($[99] !== config.matchupImageSize) {
    t17 = {
      imageBorderRadius: "none",
      imageSize: config.matchupImageSize,
      typography: t16
    };
    $[99] = config.matchupImageSize;
    $[100] = t17;
  } else {
    t17 = $[100];
  }
  let t18;
  if ($[101] !== t17 || $[102] !== t7 || $[103] !== t8 || $[104] !== t9) {
    t18 = {
      image: t7,
      name: t8,
      subtitle: t9,
      styles: t17
    };
    $[101] = t17;
    $[102] = t7;
    $[103] = t8;
    $[104] = t9;
    $[105] = t18;
  } else {
    t18 = $[105];
  }
  let t19;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      background: "",
      gap: "4",
      centerTypography: "text-sm opacity-70 font-medium"
    };
    $[106] = t19;
  } else {
    t19 = $[106];
  }
  let t20;
  if ($[107] !== T0 || $[108] !== centerContent || $[109] !== t10 || $[110] !== t11 || $[111] !== t18) {
    t20 = <T0 variant={t10} leftEntity={t11} rightEntity={t18} leftBackground="" rightBackground="" overlay="" centerContent={centerContent} styles={t19} />;
    $[107] = T0;
    $[108] = centerContent;
    $[109] = t10;
    $[110] = t11;
    $[111] = t18;
    $[112] = t20;
  } else {
    t20 = $[112];
  }
  let t21;
  if ($[113] !== isFinished || $[114] !== isLive || $[115] !== market || $[116] !== match.fansUnitedMatchId || $[117] !== match.id || $[118] !== showOdds) {
    t21 = showOdds && !isFinished && market !== "none" && <HeaderMarketOdds matchId={match.fansUnitedMatchId || match.id} preferredMarket={market} isLive={isLive} className="px-4 pb-3 mt-4" />;
    $[113] = isFinished;
    $[114] = isLive;
    $[115] = market;
    $[116] = match.fansUnitedMatchId;
    $[117] = match.id;
    $[118] = showOdds;
    $[119] = t21;
  } else {
    t21 = $[119];
  }
  let t22;
  if ($[120] !== t12 || $[121] !== t13 || $[122] !== t20 || $[123] !== t21) {
    t22 = <div className={t12}>{t13}{t20}{t21}</div>;
    $[120] = t12;
    $[121] = t13;
    $[122] = t20;
    $[123] = t21;
    $[124] = t22;
  } else {
    t22 = $[124];
  }
  let t23;
  if ($[125] !== t14 || $[126] !== t22) {
    t23 = <div className={t14}>{t22}</div>;
    $[125] = t14;
    $[126] = t22;
    $[127] = t23;
  } else {
    t23 = $[127];
  }
  let t24;
  if ($[128] !== t15 || $[129] !== t23) {
    t24 = <div className={t15}>{t23}</div>;
    $[128] = t15;
    $[129] = t23;
    $[130] = t24;
  } else {
    t24 = $[130];
  }
  return t24;
};